import { BellRing, Download, FileText, Search, Tag } from "lucide-react";
import { asArray, formatDate } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { FeaturedStats } from "@/components/admin/FeaturedStats";
import { DownloadTrends } from "@/components/admin/DownloadTrends";
import { LeadInsights } from "@/components/admin/LeadInsights";
import { CountUp } from "@/components/CountUp";

const Stat = ({ label, value, icon: Icon, accent, testId }) => (
  <div className="rounded-3xl border border-ph-line bg-ph-surface p-6 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-ph-accent/40">
    <div className="flex items-center justify-between">
      <p className="font-mono-ph text-xs uppercase tracking-wider text-ph-muted">{label}</p>
      <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${accent}22`, color: accent }}><Icon className="h-4 w-4" /></span>
    </div>
    <CountUp value={value ?? 0} className="mt-4 block font-display text-4xl font-extrabold text-ph-ink" testId={testId} />
  </div>
);

const Panel = ({ title, children, testId }) => (
  <div className="rounded-3xl border border-ph-line bg-ph-surface p-6" data-testid={testId}>
    <p className="font-display text-lg font-bold text-ph-ink">{title}</p>
    <div className="mt-4">{children}</div>
  </div>
);

const Empty = ({ children }) => <p className="py-6 text-center text-sm text-ph-muted">{children}</p>;

const Bars = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return rows.length ? (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.label}>
          <div className="flex justify-between text-sm"><span className="font-medium text-ph-ink">{r.label}</span><span className="font-mono-ph text-ph-muted">{r.count}</span></div>
          <div className="mt-1.5 h-2 rounded-full bg-ph-raised"><div className="h-2 rounded-full bg-gradient-to-r from-ph-violet to-ph-lime" style={{ width: `${(r.count / max) * 100}%` }} /></div>
        </li>
      ))}
    </ul>
  ) : <Empty>No data yet</Empty>;
};

const DocRows = ({ docs, metric }) => (docs.length ? (
  <ul className="divide-y divide-ph-line">
    {docs.map((d) => (
      <li key={d.id} className="flex items-center justify-between gap-3 py-3">
        <div className="min-w-0"><p className="truncate text-sm font-medium text-ph-ink">{d.title}</p><p className="text-xs text-ph-muted">{d.brand_name} · {d.category}</p></div>
        <span className="shrink-0 font-mono-ph text-xs text-ph-muted">{metric(d)}</span>
      </li>
    ))}
  </ul>
) : <Empty>No documents yet</Empty>);

export function Overview() {
  const { data } = useFetch("/admin/stats", {});
  const s = data || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Brands" value={s.total_brands} icon={Tag} accent="#7388FF" testId="stat-total-brands" />
        <Stat label="Price lists" value={s.total_documents} icon={FileText} accent="#7388FF" testId="stat-total-documents" />
        <Stat label="Downloads" value={s.total_downloads} icon={Download} accent="#8FD63F" testId="stat-total-downloads" />
        <Stat label="Searches" value={s.total_searches} icon={Search} accent="#5CB8E6" testId="stat-total-searches" />
        <Stat label="Alert followers" value={s.total_followers} icon={BellRing} accent="#8FD63F" testId="stat-total-followers" />
      </div>
      <DownloadTrends />
      <LeadInsights />
      <FeaturedStats rows={s.featured_stats} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Most searched brands" testId="panel-most-searched"><Bars rows={asArray(s.most_searched_brands).map((b) => ({ label: b.name, count: b.count }))} /></Panel>
        <Panel title="Top search terms" testId="panel-top-queries"><Bars rows={asArray(s.top_queries).map((q) => ({ label: q.query, count: q.count }))} /></Panel>
        <Panel title="Most downloaded documents" testId="panel-most-downloaded"><DocRows docs={asArray(s.most_downloaded)} metric={(d) => `${d.download_count} downloads`} /></Panel>
        <Panel title="Recent uploads" testId="panel-recent-uploads"><DocRows docs={asArray(s.recent_uploads)} metric={(d) => formatDate(d.created_at)} /></Panel>
      </div>
    </div>
  );
}
